// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { fleetCoverageTone } from '../../services/sopGovernanceSelectors';
import type { CoverageResult } from '../../types/sopGovernance';
import CoverageBar from './CoverageBar';
import { MissingNote, MissingValue } from '../common/MissingValue';

/** Why the binding numbers are absent. Stated once, shown wherever they'd go. */
const NO_BINDING = 'the Digital Twin publishes no hasSOP attribute on equipment';

/**
 * Why the third panel is always a gap. The binding's `covUnbindable` counts SOPs
 * that match no entity in the Knowledge Graph; deciding that needs the same
 * binding state the first two panels are missing, so the panel is drawn and its
 * gap named rather than left off the band.
 */
const NO_UNBINDABLE = 'no contract reports whether a SOP matches any entity';

/**
 * The binding's three-panel summary band: headline coverage, equipment without a
 * SOP, unbindable SOPs.
 *
 * All three panels render in both states. When binding is unpublished the
 * denominator is still real — the twin did return equipment — so the band shows
 * the count it has and marks the numerator as missing, rather than disappearing
 * and taking the design with it.
 */
export default function CoverageSummary({ coverage }: { coverage: CoverageResult }) {
  const measured = coverage.state === 'measured' ? coverage.rollup : null;
  const tone = measured ? fleetCoverageTone(measured.pct) : 'neutral';
  // Bindable equipment found, which is real either way: it is the rollup's
  // denominator when measured, and the unpublished variant's own total when not.
  const equipmentFound = coverage.state === 'measured' ? coverage.rollup.total : coverage.total;

  return (
    <div className="sg-summary">
      <article className="sg-summary-coverage" data-testid="sg-fleet-coverage">
        <div className="sg-summary-head">
          <span className="sg-label">Fleet SOP binding coverage</span>
          {measured ? (
            <strong className={`sg-summary-pct sg-tone-${tone}`}>{measured.pct}%</strong>
          ) : (
            <strong className="sg-summary-pct sg-tone-neutral">
              <MissingValue reason={NO_BINDING} />
            </strong>
          )}
        </div>
        {/* The track is drawn either way — an empty track reads as "unmeasured",
            a zero-width fill on a coloured track would read as 0% coverage. */}
        <CoverageBar pct={measured?.pct ?? 0} tone={tone} tall />
        {measured ? (
          <p className="sg-summary-note">
            {measured.bound} of {measured.total} equipment items have a bound SOP.
          </p>
        ) : (
          <MissingNote>
            {equipmentFound} bindable equipment items found across the fleet. How many are bound is
            unknown — {NO_BINDING}.
          </MissingNote>
        )}
      </article>

      <article className="sg-tile sg-tile--warn" data-testid="sg-tile-equipment-without-sop">
        <strong>
          {measured ? measured.equipmentWithoutSop : <MissingValue reason={NO_BINDING} />}
        </strong>
        <span className="sg-label">Equipment without SOP</span>
      </article>

      <article className="sg-tile sg-tile--neutral" data-testid="sg-tile-unbindable">
        <strong>
          <MissingValue reason={NO_UNBINDABLE} />
        </strong>
        <span className="sg-label">Unbindable SOPs</span>
      </article>
    </div>
  );
}
